import {
    Feedback360SettingCard,
    type Feedback360SettingChoice,
} from '@/components/Feedback360SettingCard/Feedback360SettingCard';
import {
    Feedback360SettingsLock,
    type Feedback360SettingsLockValue,
} from '@/constants/review/Feedback360Settings';
import { useAmelioTranslations } from '@/hooks';

// A radio group carries strings: each one stands for a value of the boolean setting.
const ENABLED = 'enabled';
const DISABLED = 'disabled';
type RepliesChoice = typeof ENABLED | typeof DISABLED;

export interface FeedbackCommentRepliesCardProps {
    value: boolean;
    onChange?: (commentRepliesEnabled: boolean) => void;
    /** Absent while the choice is still open. A launch does not close it: replies can change until the 360 is over. */
    lock?: Feedback360SettingsLockValue;
    className?: string;
    dataTestId?: string;
}

/**
 * Whether the readers of a 360's results can reply to its respondents' comments. Respondents comment either
 * way, and the choice can change until the 360 is over, launched or not.
 */
export const FeedbackCommentRepliesCard = ({
    value,
    onChange,
    lock,
    className,
    dataTestId = 'feedback-comment-replies-card',
}: FeedbackCommentRepliesCardProps) => {
    const { t } = useAmelioTranslations();

    const choices: Feedback360SettingChoice<RepliesChoice>[] = [
        {
            value: ENABLED,
            label: t('feedbackComments.card.enabled.title'),
            iconName: 'messages-message-text',
            meaning: t('feedbackComments.card.enabled.managersReply'),
        },
        {
            value: DISABLED,
            label: t('feedbackComments.card.disabled.title'),
            iconName: 'messages-message-remove',
            meaning: t('feedbackComments.card.disabled.noReply'),
        },
    ];

    const isInherited = lock === Feedback360SettingsLock.INHERITED;
    const isChoiceClosed = isInherited || lock === Feedback360SettingsLock.COMPLETED;

    const lockNotices: Record<Feedback360SettingsLockValue, string | undefined> = {
        [Feedback360SettingsLock.LAUNCHED]: undefined,
        [Feedback360SettingsLock.INHERITED]: undefined,
        [Feedback360SettingsLock.COMPLETED]: t('feedbackComments.card.completedNotice'),
    };

    return (
        <Feedback360SettingCard
            name={'feedback-comment-replies'}
            title={t('feedbackComments.card.title')}
            description={t(
                isChoiceClosed
                    ? 'feedbackComments.card.fixedDescription'
                    : 'feedbackComments.card.description',
            )}
            iconName={'messages-message-text'}
            choices={choices}
            value={value ? ENABLED : DISABLED}
            onChange={(choice) => onChange?.(choice === ENABLED)}
            lockNotice={lock && lockNotices[lock]}
            fixedReason={isInherited ? t('feedbackPrivacy.card.inheritedTooltip') : undefined}
            className={className}
            dataTestId={dataTestId}
        />
    );
};
